import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { Choice } from "@comunicare/comunicare-angular-filters-display";
import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import {
  BILLING_TYPE,
  DataType,
  IAggregatedChargeItemsInfo,
  IChargeItemsInfo,
  IFilter,
  IPreferenceUser,
  ITableParameter,
  PreferenceContext,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject } from "rxjs";
import { Subscription } from "rxjs/internal/Subscription";
import { first, takeUntil } from "rxjs/operators";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PdfTypeChoiceComponent, PDF_TYPE_CONTEXT } from "src/app/components/patient-QR/pdf-type-choice/pdf-type-choice.component";
import { HelpData } from "src/app/helpers/helpData";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { BillingDataSource } from "../../billing.datasource";
import { BillingService } from "../../services/billing.service";
import { InvoiceAggregatedTableComponent } from "../invoice-aggregated-table/invoice-aggregated-table.component";
import { InvoiceTableComponent } from "../invoice-table/invoice-table.component";

@Component({
  selector: "app-billing-page",
  templateUrl: "./billing-page.component.html",
  styleUrls: ["./billing-page.component.scss"],
})
export class BillingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  protected onDestroy$ = new Subject<void>();

  private preferences: ITableParameter;

  public dataSource;
  public totalCount = 0;
  public subscriptions: Subscription[] = [];
  public sort: MatSort;

  public BILLING_TYPE = BILLING_TYPE;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(InvoiceTableComponent) invoiceTable: InvoiceTableComponent;
  @ViewChild(InvoiceAggregatedTableComponent) invoiceAggregatedTable: InvoiceAggregatedTableComponent;

  public currentServices: string[] = [];
  public healthcareServicesRefs: string[];
  public filters: IFilter[] = [];
  /** Raw active filters, kept in sync to feed `app-item-filter` (via the `filterFor` pipe) in the invoice table. */
  public activeFilters: IFilter[] = [];
  private navigationFilter: IFilter;
  public currentPageSize: number;
  public isMobile: boolean;
  public data: IChargeItemsInfo[] | IAggregatedChargeItemsInfo[];
  public availableColumnItems: Item[] = [
    { value: "lastname", display: "model.patient.name", checked: true },
    { value: "firstname", display: "model.patient.firstname", checked: true },
    { value: "ssin", display: "forms.SSIN", checked: true },
    { value: "birthDate", display: "table.birthdate", checked: true },
    { value: "category", display: "table.category", checked: false },
    { value: "startDate", display: "table.start", checked: true },
    { value: "endDate", display: "table.end", checked: true },
    { value: "code", display: "table.code", checked: false },
    { value: "totalPrice", display: "table.amount", checked: true },
    { value: "adherence", display: "table.adherence", checked: true },
    { value: "status", display: "table.status", checked: true },
    { value: "careplan", display: "table.careplan", checked: false },
    { value: "prescriptionStart", display: "table.prescriptionStart", checked: true },
    { value: "prescriptionDuration", display: "table.prescriptionDuration", checked: true },
    { value: "prescriptionStatus", display: "table.prescriptionStatus", checked: true },
    { value: "billingCycle", display: "table.billingCycle", checked: true },
    { value: "primoPrescriptionStart", display: "table.primoPrescriptionStart", checked: true },
    { value: "internalId", display: "table.internalId", checked: false },
    { value: "pseudoname", display: "forms.pseudoname", checked: false },
    { value: "actions", display: "table.action", checked: true },
  ];

  public DATA_TYPE = DataType;
  public actionOnMultiple = false;
  public hasCheckboxTicked: boolean;
  public commonTransitions: BILLING_TYPE[];
  public aggregatedView = undefined;
  public isLoading = true;
  private invoiceAggregatedSelectionChangedObs;

  constructor(
    private billingService: BillingService,
    private sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    public responsiveService: ResponsiveService,
    private route: ActivatedRoute,
    private preferenceService: PreferenceService,
    private cdr: ChangeDetectorRef,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
    private helpData: HelpData
  ) {
    this.preferences = this.route.snapshot.data.preferences;
    this.navigationFilter = this.router.getCurrentNavigation()?.extras?.state?.filter;
  }

  ngOnInit(): void {
    this.dataSource = new BillingDataSource(this.billingService);

    this.dataSource.rawData$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      this.isLoading = false;
      if (this.aggregatedView) {
        this.data = data as IAggregatedChargeItemsInfo[];
        this.cdr.detectChanges();
        this.invoiceAggregatedTable.selection.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
          this.updateCommonTransitions();
          this.hasCheckboxTicked = this.invoiceAggregatedTable.selection.selected.length > 0;
        });
      } else {
        this.data = data as IChargeItemsInfo[];
        this.cdr.detectChanges();

        this.invoiceTable.selection.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
          this.updateCommonTransitions();
          this.hasCheckboxTicked = this.invoiceTable.selection.selected.length > 0;
        });
      }
    });
    if (!Tools.isDefined(this.navigationFilter)) {
      // apply filter from preference
      this.filters = this.preferences?.filters ? this.preferences.filters : [];
      this.filters?.forEach((filter) => this.dataSource.setFilter(filter));
      this.activeFilters = [...this.dataSource.getAllFilters()];
    }
    if (this.preferences?.availableColumnItems?.length) {
      // apply available columns from preference
      // remove unexisting column saved in preferences
      const savedColumns = this.preferences.availableColumnItems.filter((saved) =>
        this.availableColumnItems.some((column) => column.value === saved.value)
      );
      // add the columns added since the preference was saved
      const newColumns = this.availableColumnItems.filter((column) => !savedColumns.some((saved) => saved.value === column.value));
      this.availableColumnItems = [...savedColumns, ...newColumns];
    }

    this.responsiveService.isHandset$.pipe(takeUntil(this.onDestroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;

      if (this.healthcareServicesRefs?.length) {
        this.loadData();
      }
    });
  }

  ngAfterViewInit(): void {
    //  get pageSize from preference

    this.paginator.pageSize = this.preferences?.itemsPerPage ? this.preferences.itemsPerPage : 25;
    this.invoiceTable.setDisplayedColumns(this.availableColumnItems);
    this.cdr.detectChanges();

    // watch page change
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe((_page: PageEvent) => {
      this.loadData();
      this.updatePreference();
    });

    // Reactive case: when the services are initialized after the view loads
    this.sessionService.servicesSetupWatch.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.updateServicesRefsAndLoadData();
    });

    // Immediate case: if the services are already available when component loads
    if (this.sessionService.currentService) {
      this.updateServicesRefsAndLoadData();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private updateServicesRefsAndLoadData(): void {
    if (this.sessionService.currentService.reference === "all") {
      this.healthcareServicesRefs = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    } else {
      this.healthcareServicesRefs = [this.sessionService.currentService?.reference];
    }
    this.loadData();
    this.cdr.detectChanges();
  }

  loadData(): void {
    if (this.navigationFilter) {
      this.dataSource.setFilter(this.navigationFilter);
      this.filters = Tools.deepCopy(this.dataSource.getAllFilters());
      this.activeFilters = [...this.dataSource.getAllFilters()];
    }
    this.dataSource.loadData({
      filters: this.responsiveService.isMobile ? [] : this.getFiltersFormated(), //no filter on mobile view
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      pageNumber: this.paginator?.pageIndex,
      pageSize: this.paginator?.pageSize,
      services: this.healthcareServicesRefs,
      aggregated: this.aggregatedView,
    });

    if (!this.aggregatedView) {
      this.billingService.getInvoicesCount(this.healthcareServicesRefs, this.getFiltersFormated()).subscribe((res) => {
        this.totalCount = res.count;
      });
      this.invoiceTable?.selection?.clear();
    } else {
      this.invoiceAggregatedTable?.selection?.clear();
    }
  }

  onSortChanged($event: MatSort): void {
    this.sort = $event;
    this.loadData();
  }

  applyFilter(filter: IFilter): void {
    this.navigationFilter = undefined;
    this.dataSource.setFilter(filter);
    this.filters = Tools.deepCopy(this.dataSource.getAllFilters());
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.loadData();
    this.updatePreference();
  }

  clearFilter(): void {
    this.navigationFilter = undefined;
    this.dataSource.clearFilter();
    this.filters = [];
    this.activeFilters = [];
    this.paginator.pageIndex = 0;
    this.loadData();
    this.updatePreference();
  }
  /**
   * Preferences
   */
  public updatePreference(): Promise<void> {
    if (this.responsiveService.isMobile) {
      return; // we don't want to save filters and other preferences on smartphone since we don't have the possibility to change filter on that view
    }

    return this.preferenceService
      .update({
        context: PreferenceContext.INVOICE_LIST,
        parameters: {
          filters: this.dataSource.getAllFilters(),
          itemsPerPage: this.paginator.pageSize,
          availableColumnItems: this.availableColumnItems,
        } as ITableParameter,
      })
      .pipe(first())
      .toPromise()
      .then((parameters: IPreferenceUser) => {
        const param = parameters.preferences.find((p) => p.context === PreferenceContext.INVOICE_LIST);
        this.preferences = param.parameters;
      });
  }
  activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
    if (this.aggregatedView) {
      this.invoiceAggregatedTable.activateCheckBox();
    } else {
      this.invoiceTable.activateCheckBox();
    }
  }

  public async availableColumnChanged(_items: Item[]): Promise<void> {
    if (this.aggregatedView) {
      this.invoiceAggregatedTable.setDisplayedColumns(this.availableColumnItems);
    } else {
      this.invoiceTable.setDisplayedColumns(this.availableColumnItems);
    }
    await this.updatePreference();
  }

  private getFiltersFormated(): IFilter[] {
    const formatedFilters: IFilter[] = [];
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        switch (filter.dataType) {
          case this.DATA_TYPE.DATE:
            // format date params to string
            filter.data.fromDate = moment(filter.data.fromDate).format("YYYY-MM-DD");
            filter.data.toDate = moment(filter.data.toDate).format("YYYY-MM-DD");
            break;
          case this.DATA_TYPE.CHOICE: {
            const values: Array<string> = [];
            const choices: Choice[] = filter.data ? filter.data : [];
            choices.forEach((choice) => {
              if (choice.checked) {
                values.push(choice.value);
              }
            });
            filter.data = {
              value: values,
            };
            break;
          }
        }
        formatedFilters.push(filter);
      });
    }
    this.filters = formatedFilters;
    return formatedFilters;
  }

  public downloadSelected(): void {
    const chargeItemIds: string[] = this.aggregatedView
      ? this.invoiceAggregatedTable.selection.selected.map((item) => item.identification)
      : this.invoiceTable.selection.selected.map((item) => item.identifier[0].value);

    this.responsiveDialog.open(
      PdfTypeChoiceComponent,
      {
        data: {
          context: PDF_TYPE_CONTEXT.CHARGE_ITEM,
          chargeItemIds,
          columns: this.getColumnNames(),
          isAggregatedView: this.aggregatedView,
        },
        disableClose: true,
      },
      {},
      {
        minWidth: "auto",
        width: "auto",
        maxWidth: "auto",

        minHeight: "auto",
        height: "auto",
        maxHeight: "auto",
      }
    );
  }

  public billSelected(): void {
    const chargeItemIds: string[] = this.aggregatedView
      ? this.invoiceAggregatedTable.selection.selected.map((item) => item.identification)
      : this.invoiceTable.selection.selected.map((item) => item.identifier[0].value);

    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: this.translateService.instant("page.invoices.warning.billSelected"),
        type: ConfirmationDialogType.CONFIRM,
      },
    });

    dialog.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.sendChargeItem(chargeItemIds);
      }
    });
  }

  // Send one Charge item when (send) emit from invoice-table
  public onSend(chargeItemId: string): void {
    this.sendChargeItem([chargeItemId]);
  }

  private sendChargeItem(chargeItemIds) {
    this.billingService
      .sendChargeItems(chargeItemIds)
      .pipe(first())
      .subscribe(() => {
        this.loadData();
      });
  }

  updateCommonTransitions(): void {
    const selected = this.aggregatedView ? this.invoiceAggregatedTable.selection.selected : this.invoiceTable.selection.selected;

    if (selected.length === 0) {
      this.commonTransitions = [];
      return;
    }

    let intersection = new Set(this.billingService.getAvailableStatusTransitions(selected[0].status));
    for (let i = 1; i < selected.length; i++) {
      const current = new Set(this.billingService.getAvailableStatusTransitions(selected[i].status));
      intersection = new Set([...intersection].filter((x) => current.has(x)));
    }

    this.commonTransitions = Array.from(intersection);
  }

  bulkUpdateStatus(newStatus: BILLING_TYPE): void {
    const chargeItems = Tools.deepCopy(
      this.aggregatedView ? this.invoiceAggregatedTable.selection.selected : this.invoiceTable.selection.selected
    );

    if (!this.commonTransitions.includes(newStatus)) {
      console.warn(`Status ${newStatus} not applicable to selected items`);
      return;
    }

    for (const row of chargeItems) {
      row.status = newStatus;
    }

    const chargeItemIds: string[] = this.aggregatedView
      ? chargeItems.map((item) => item.identification)
      : chargeItems.map((item) => item.identifier[0].value);
    this.billingService
      .updateChargeItemStatus(chargeItemIds, newStatus)
      .pipe(first())
      .subscribe(() => {
        this.loadData();
        if (this.aggregatedView) {
          this.invoiceAggregatedTable.selection.clear();
        } else {
          this.invoiceTable.selection.clear();
        }
      });
  }

  onUpdateStatus($event: { identifiers: string[]; newStatus: BILLING_TYPE }): void {
    this.billingService
      .updateChargeItemStatus($event.identifiers, $event.newStatus)
      .pipe(first())
      .subscribe(() => {
        this.loadData();
        if (this.aggregatedView) {
          this.invoiceAggregatedTable.selection.clear();
        } else {
          this.invoiceTable.selection.clear();
        }
      });
  }

  public openBillingPageHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.billingPageHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public onSwitchAggregatedView(): void {
    this.isLoading = true;
    this.aggregatedView = !this.aggregatedView;
    this.hasCheckboxTicked = false;
    this.actionOnMultiple = false;
    this.cdr.detectChanges();
    this.loadData();
  }

  public recalculateAdherence(chargeItemId?: string): void {
    let chargeItemIds: string[];
    if (!chargeItemId) {
      if (!this.actionOnMultiple) {
        chargeItemIds = this.aggregatedView
          ? (this.data as IAggregatedChargeItemsInfo[]).flatMap((item) => item.chargeItems.map((chargeItem) => chargeItem.identification))
          : (this.data as IChargeItemsInfo[]).map((item) => item.identifier[0].value);
      } else {
        chargeItemIds = this.aggregatedView
          ? this.invoiceAggregatedTable.selection.selected.map((item) => item.identification)
          : this.invoiceTable.selection.selected.map((item) => item.identifier[0].value);
      }
    }
    this.billingService
      .recalculateAdherences(chargeItemId ? [chargeItemId] : chargeItemIds)
      .pipe(first())
      .subscribe(() => {
        this.loadData();
      });
  }

  public changeColumnOrder(availableColumnItems: Item[]): void {
    this.availableColumnItems = availableColumnItems;
    this.updatePreference();
  }

  private getColumnNames(): string[] {
    if (this.aggregatedView) {
      return undefined;
    }
    const names = [];
    this.availableColumnItems
      .filter((c) => c.checked)
      .forEach((column) => {
        switch (column.value) {
          case "category":
            names.push("prescription.category");
            break;
          case "prescriptionStart":
            names.push("prescription.startDate");
            break;
          case "prescriptionDuration":
            names.push("prescription.duration");
            break;
          case "prescriptionStatus":
            names.push("prescription.status");
            break;
          case "billingCycle":
            names.push("prescription.pricingPeriodicity");
            break;
          case "primoPrescriptionStart":
            names.push("prescription.primoPrescriptionStart");
            break;
          case "careplan":
            names.push("officialCareplan");
            break;
          default:
            if (column.value !== "actions") {
              names.push(column.value);
            }
            break;
        }
      });
    return names;
  }
}
